import CardHeader from "components/CardHeader";
import { Account } from "models/Account";
import { Invoice } from "models/Invoice";
import React from "react";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody } from "reactstrap";
import { useOfficeTranslation } from "i18n/OfficeTranslation";
import { OfficeTranslationKey } from "i18n/officeTranslations";

interface Props {
    invoices: Invoice[];
    accountById: Map<string, Account>;
    currency: string;
}

const invoiceStatus = (invoice: Invoice): { color: string; labelKey: OfficeTranslationKey } => {
    if (invoice.IsPaid()) return { color: "success", labelKey: "common.status.paid" };
    if (invoice.draft) return { color: "light", labelKey: "common.status.draft" };
    if (invoice.IsPayWhenPaid()) return { color: "warning", labelKey: "common.status.payWhenPaid" };
    if (invoice.GetDeadlineDate().getTime() < Date.now()) return { color: "danger", labelKey: "common.status.overdue" };
    return { color: "warning", labelKey: "common.status.outstanding" };
};

const ProjectInvoicesCard: React.FC<Props> = ({ invoices, accountById, currency }) => {
    const { hasProvider, intlLocale, t } = useOfficeTranslation();
    const money = new Intl.NumberFormat(hasProvider ? intlLocale : "nl-NL", { style: "currency", currency });

    return <Card>
        <CardBody>
            <CardHeader title={t("common.invoices")} icon="mdi-file-document-outline" count={invoices.length} />
            {invoices.map(invoice => {
                const status = invoiceStatus(invoice);
                return <div
                    className="d-flex justify-content-between align-items-center gap-3 border-bottom py-3"
                    key={invoice.id}
                >
                    <div className="min-w-0">
                        <Link className="fw-semibold d-block text-truncate" to={`/invoice/${invoice.id}`}>
                            {invoice.GetDisplayName() || invoice.id}
                        </Link>
                        <small className="text-muted d-block text-truncate">
                            {invoice.supplier
                                ? accountById.get(invoice.supplier)?.legal_name ?? invoice.supplier
                                : t("common.tenant")}
                            {" → "}
                            {accountById.get(invoice.customer)?.legal_name ?? invoice.customer}
                            {" · "}
                            {invoice.GetCreatedDate().toLocaleDateString(intlLocale)}
                        </small>
                    </div>
                    <div className="text-end flex-shrink-0">
                        <Badge color={status.color} className="mb-1">{t(status.labelKey)}</Badge>
                        <div>{money.format(invoice.CalcGrandTotal())}</div>
                    </div>
                </div>;
            })}
            {!invoices.length &&
                <p className="text-muted mt-3 mb-0">{t("projects.projectInvoicesCard.noHoursHaveBeenInvoicedForThisProjectYet")}</p>
            }
        </CardBody>
    </Card>;
};

export default ProjectInvoicesCard;
